import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import Breadcrumb from '../components/Breadcrumb';
import Pagination from '../components/main/Pagination';
import useRideReportApi from '../api/useRideReportApi';
import { debounce } from '../helper/debounce';
import CommonLoader from '../components/loaders/CommonLoader';
import { Avatar, Dialog, Button, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { useAuth } from '../contexts/AuthContext';

const StatusBadge = ({ status }) => {
    const statusStyles = {
        approved: {
            text: "Approved",
            className:
                "bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 fw-medium text-sm",
        },
        pending: {
            text: "Pending",
            className:
                "bg-warning-focus text-warning-600 border border-warning-main px-24 py-4 radius-4 fw-medium text-sm",
        },
        review: {
            text: "In Review",
            className:
                "bg-info-focus text-info-600 border border-info-main px-24 py-4 radius-4 fw-medium text-sm",
        },
        rejected: {
            text: "Rejected",
            className:
                "bg-danger text-white border border-danger px-24 py-4 radius-4 fw-medium text-sm",
        },
    };

    const statusInfo = statusStyles[status] || {
        text: status || "Unknown",
        className:
            "bg-gray-200 text-gray-600 border border-gray-400 px-24 py-4 radius-4 fw-medium text-sm",
    };

    return <span className={statusInfo.className}>{statusInfo.text}</span>;
};

const ReportsPage = () => {
    const [selectedReport, setselectedReport] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { getRideReportData, loading, approveRejectRideReport, addRideReport } = useRideReportApi();
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [openApprove, setOpenApprove] = useState(false);
    const [openReject, setOpenReject] = useState(false);
    const [reason, setReason] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const mainSwiperRef = useRef(null);
    const { isActionAllowed, profile } = useAuth();
    const [openAdd, setOpenAdd] = useState(false);
    const [addPhone, setAddPhone] = useState("");
    const [addReason, setAddReason] = useState("");
    const [addImages, setAddImages] = useState([]);

    const handleThumbnailClick = (index) => {
        setActiveIndex(index);
        if (mainSwiperRef.current) {
            mainSwiperRef.current.swiper.slideTo(index);
        }
    };

    const handleApprove = (id) => {
        setselectedReport(id);
        setOpenApprove(true)
    };

    const handleDeny = (id) => {
        setselectedReport(id);
        setOpenReject(true)
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        setSelectedStatus(value);
        fetchRideReports(1, searchTerm, value);
    };

    const fetchRideReports = async (page = 1, search = '', selectedStatus) => {
        const res = await getRideReportData(page, search, selectedStatus);
        const payload = res.data.payload;
        setData(payload.list_of_fraud_history || payload.list_of_request || []);
        setTotalPages(payload.total_pages || 1);
    };

    const debouncedfetchRideReports = useCallback(
        debounce((value) => {
            fetchRideReports(1, value);
            setPage(1);
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedfetchRideReports(value);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        fetchRideReports(page, searchTerm, selectedStatus);
    }, [page]);

    const openDialog = (suggestion) => {
        setselectedReport(suggestion);
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setselectedReport(null);
        setIsDialogOpen(false);
    };

    const handleApproveSubmit = async () => {
        setOpenApprove(false);
        const payload = {
            "status": 'approved',
        }
        await approveRejectRideReport(selectedReport, payload);
        fetchRideReports(page, searchTerm, selectedStatus);
    };

    const handleRejectSubmit = async () => {
        if (!reason.trim()) {
            toast.error("Please enter a rejection reason.");
            return;
        }
        const payload = {
            "status": 'rejected',
            "reason": reason
        }
        setOpenReject(false);
        await approveRejectRideReport(selectedReport, payload);
        fetchRideReports(page, searchTerm, selectedStatus);
        setReason("")
    };
    const handleAddSubmit = async () => {
        if (!addPhone.trim()) {
            toast.error('Please enter phone number.');
            return;
        }
        const formData = new FormData();
        addImages.forEach((file) => {
            formData.append('fraud_proof_imgs', file);
        });
        formData.append('phone_number', addPhone);
        formData.append('reason', addReason);
        try {
            await addRideReport(formData);
            toast.success('Report added successfully');
            setOpenAdd(false);
            fetchRideReports(page, searchTerm, selectedStatus);
            setAddPhone('');
            setAddReason('');
            setAddImages([]);
        } catch (err) {
            toast.error(err.message || 'Error adding report');
        }
    };

    return (
        <div>
            <Breadcrumb title="Fraud History" />
            <div className="card h-100 p-0 radius-12 mt-24">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
                        <form className="navbar-search me-2" onSubmit={(e) => e.preventDefault()}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </form>
                        <select
                            className="form-select form-select-sm me-2"
                            value={selectedStatus}
                            onChange={handleStatusChange}
                        >
                            <option value="" disabled>Select Status</option>
                            <option value="">All</option>
                            <option value="review">In Review</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        {(profile?.user_type === 'admin' || profile?.user_type === 'superadmin') && (
                            <button type="button" className="btn btn-primary ms-2" onClick={() => setOpenAdd(true)}>
                                Add Ride Report
                            </button>
                        )}
                    </div>
                </div>
                <div className="card-body p-24">
                    {loading ? (
                        <CommonLoader />
                    ) : (
                        <>
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-start">User</th>
                                            <th>Fraud No.</th>
                                            <th>Created At</th>
                                            <th>Status</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.length > 0 ? (
                                            data.map((item) => (
                                                <tr key={item?._id}>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Avatar
                                                                src={item?.user_id?.profile_photo}
                                                                alt=""
                                                                className="w-40-px h-40-px rounded-circle me-12"
                                                            />
                                                            <div className="d-flex flex-column align-items-start">
                                                                <span className="text-md text-secondary-light fw-medium">
                                                                    {item?.user_id?.user_name || "-"}
                                                                </span>
                                                                <span className="text-sm ">
                                                                    {item?.user_id?.phone_number || "-"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">{item?.phone_number || "-"}</td>
                                                    <td className="text-center">
                                                        {dayjs(item?.createdAt).format('DD MMM YYYY hh:mm A')}
                                                    </td>
                                                    <td className="text-center">
                                                        <StatusBadge status={item?.status} />
                                                    </td>
                                                    <td className="text-center d-flex gap-2 justify-content-center align-items-center">
                                                        {isActionAllowed('RideReports', 'view') ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => openDialog(item)}
                                                                className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon
                                                                    icon="majesticons:eye-line"
                                                                    className="icon text-xl"
                                                                />
                                                            </button>
                                                        ) : null}
                                                        {isActionAllowed('RideReports', 'edit') ? (
                                                            <button
                                                                onClick={() => handleApprove(item._id)}
                                                                className="bg-success-focus bg-hover-success-200 text-success-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon icon="mdi:check-bold" className="text-xl" />
                                                            </button>
                                                        ) : null}
                                                        {isActionAllowed('RideReports', 'edit') ? (
                                                            <button
                                                                onClick={() => handleDeny(item._id)}
                                                                className="bg-danger-focus bg-hover-danger-200 text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon icon="mdi:close-thick" className="text-xl" />
                                                            </button>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">No Suggestions Found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                                <span>Page {page} of {total_pages}</span>
                                <Pagination
                                    total_pages={total_pages}
                                    current_page={page}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* MUI Dialog with Bootstrap Card */}
            <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <div className="card mb-0">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">View Fraud Report</h6>
                        <button type="button" className="btn btn-sm btn-light p-1" onClick={closeDialog}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body">
                        {/* Reporter info */}
                        <div className="d-flex align-items-center gap-3 mb-16 pb-12 border-bottom">
                            <Avatar
                                src={selectedReport?.user_id?.profile_photo}
                                sx={{ width: 48, height: 48 }}
                            />
                            <div>
                                <div className="fw-semibold text-md">
                                    {selectedReport?.user_id?.user_name || 'Unknown User'}
                                </div>
                                <div className="text-sm text-secondary-light">
                                    Reporter: {selectedReport?.user_id?.phone_number || '-'}
                                </div>
                            </div>
                            <div className="ms-auto">
                                <StatusBadge status={selectedReport?.status} />
                            </div>
                        </div>

                        {/* Fraud phone number */}
                        <div className="mb-12">
                            <label className="form-label fw-semibold text-sm mb-4">Reported Fraud Phone Number</label>
                            <div className="d-flex align-items-center gap-2">
                                <Icon icon="akar-icons:phone" className="text-danger" />
                                <span className="fw-bold text-danger text-lg">
                                    {selectedReport?.phone_number || '-'}
                                </span>
                            </div>
                        </div>

                        {/* Reason */}
                        <div className="mb-16">
                            <label className="form-label fw-semibold text-sm mb-4">Reason / Description</label>
                            <div className="p-12 bg-neutral-100 radius-8 text-secondary-light">
                                {selectedReport?.reason || '-'}
                            </div>
                        </div>

                        {/* Proof Images */}
                        {selectedReport?.proof_imgs?.length > 0 && (
                            <>
                                <label className="form-label fw-semibold text-sm mb-8">Proof Images</label>
                                <Swiper
                                    spaceBetween={10}
                                    slidesPerView={1}
                                    pagination={{ clickable: true }}
                                    onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                                    className="main-swiper"
                                    ref={mainSwiperRef}
                                >
                                    {selectedReport.proof_imgs.map((imgUrl, index) => (
                                        <SwiperSlide key={index}>
                                            <PhotoProvider>
                                                <PhotoView src={imgUrl}>
                                                    <img
                                                        src={imgUrl}
                                                        alt={`Proof ${index + 1}`}
                                                        className="w-100"
                                                        style={{ objectFit: 'contain', height: '300px', borderRadius: '8px' }}
                                                    />
                                                </PhotoView>
                                            </PhotoProvider>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                {/* Thumbnails */}
                                {selectedReport.proof_imgs.length > 1 && (
                                    <div style={{ display: 'flex', overflowX: 'auto', padding: '10px 0', gap: '10px' }}>
                                        {selectedReport.proof_imgs.map((imgUrl, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleThumbnailClick(index)}
                                                style={{
                                                    flexShrink: 0,
                                                    width: '50px',
                                                    height: '50px',
                                                    cursor: 'pointer',
                                                    border: activeIndex === index ? '2px solid #007bff' : '2px solid transparent',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <img
                                                    src={imgUrl}
                                                    alt={`Thumb ${index + 1}`}
                                                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Date */}
                        <div className="mt-12 text-sm text-secondary-light">
                            Reported on: {dayjs(selectedReport?.createdAt).format('DD MMM YYYY hh:mm A')}
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={openApprove}
                onClose={() => setOpenApprove(false)}
                maxWidth={false} // Disable MUI's default maxWidth
                fullWidth={false}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        width: '350px', // Custom width here
                    }
                }}
            >
                <div className="card mb-0">
                    <div className="m-2 d-flex justify-content-end align-items-center">

                        <button type="button" className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center" onClick={() => setOpenApprove(false)}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body text-center pb-24">
                        <h6 className="card-title mb-0">Approve Ride Report</h6>
                        <p className="text-center mt-3">
                            Are you sure want to approve this booking report ?
                        </p>
                        <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                            {isActionAllowed('RideReports', 'edit') ? (
                                <button onClick={handleApproveSubmit}
                                    className="btn btn-success border border-success-600 text-md px-48 py-12 radius-8">
                                    Approve
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={openReject}
                onClose={() => setOpenReject(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <div className="card mb-0">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Reject Ride Report</h6>
                        <button type="button" className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center" onClick={() => setOpenReject(false)}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body">
                        <p className="">

                            <textarea
                                className="form-control"
                                rows="4"
                                placeholder="Write reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </p>
                        <div className="d-flex justify-content-center align-items-center mt-3">
                            {isActionAllowed('RideReports', 'edit') ? (
                                <button onClick={handleRejectSubmit} className="btn btn-danger border border-danger-600 text-md px-48 py-12 radius-8">
                                    Reject
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Dialog>
            {/* Add Ride Report Dialog */}
            <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <div className="card mb-0">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Add Ride Report</h6>
                        <button type="button" className="btn btn-sm btn-light p-1" onClick={() => setOpenAdd(false)}>
                            <MdClose size={18} />
                        </button>
                    </div>
                    <div className="card-body">
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label fw-semibold text-sm mb-2">Phone Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={addPhone}
                                    onChange={(e) => setAddPhone(e.target.value)}
                                    placeholder="Enter phone number"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold text-sm mb-2">Reason</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={addReason}
                                    onChange={(e) => setAddReason(e.target.value)}
                                    placeholder="Enter reason"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold text-sm mb-2">Proof Images</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => setAddImages(Array.from(e.target.files))}
                                />
                                <div className="mt-2">
                                    {addImages.map((file, idx) => (
                                        <span key={idx} className="badge bg-secondary me-2 d-inline-flex align-items-center">
                                            {file.name}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-light ms-1 p-0"
                                                onClick={() => setAddImages(prev => prev.filter((_, i) => i !== idx))}
                                            >
                                                <MdClose size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="d-flex justify-content-center mt-3">
                                <button
                                    className="btn btn-primary px-4"
                                    onClick={handleAddSubmit}
                                    disabled={!addPhone.trim()}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ReportsPage;

